import { FieldDataType } from "@/types/dynamic-detail";

/**
 * Minimal, total evaluator for a field's `display_condition`.
 *
 * Grammar: `field op literal` terms, or a bare `field` read as truthy, combined by
 * `&&` and `||` — `&&` binding tighter. Operators: `==`, `!=`, `>`, `>=`, `<`, `<=`,
 * `in`. Literals: quoted strings, numbers, `true`, `false`, `null`.
 *
 * There is deliberately no dynamic code evaluation: an unparseable expression yields
 * `true`, so a descriptor written tomorrow shows its field rather than hiding it.
 */

const COMPARISONS = ["!==", "===", "!=", "==", ">=", "<=", ">", "<"] as const;

function parseLiteral(raw: string): unknown {
    const token = raw.trim();
    if ((token.startsWith("'") && token.endsWith("'")) || (token.startsWith('"') && token.endsWith('"'))) {
        return token.slice(1, -1);
    }
    if (token === "true") return true;
    if (token === "false") return false;
    if (token === "null") return null;
    if (token === "undefined") return undefined;
    if (token.startsWith("[") && token.endsWith("]")) {
        return token
            .slice(1, -1)
            .split(",")
            .map(part => parseLiteral(part))
            .filter(part => part !== "");
    }
    const asNumber = Number(token);
    return Number.isNaN(asNumber) || token === "" ? token : asNumber;
}

/** Loose equality so `"45" == 45` holds, which is what a form value looks like. */
export function looseEquals(left: unknown, right: unknown): boolean {
    if (left === right) return true;
    if (left == null || right == null) return (left ?? null) === (right ?? null);
    if (typeof left === "boolean" || typeof right === "boolean") return Boolean(left) === Boolean(right);
    return String(left) === String(right);
}

function compare(left: unknown, right: unknown, operator: string): boolean {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    const numeric = !Number.isNaN(leftNumber) && !Number.isNaN(rightNumber);
    switch (operator) {
        case ">":
            return numeric ? leftNumber > rightNumber : String(left) > String(right);
        case ">=":
            return numeric ? leftNumber >= rightNumber : String(left) >= String(right);
        case "<":
            return numeric ? leftNumber < rightNumber : String(left) < String(right);
        case "<=":
            return numeric ? leftNumber <= rightNumber : String(left) <= String(right);
        default:
            return false;
    }
}

function evaluateTerm(term: string, values: Record<string, unknown>): boolean {
    const trimmed = term.trim();
    if (!trimmed) return true;

    const negated = trimmed.startsWith("!") && !trimmed.startsWith("!=");
    const body = negated ? trimmed.slice(1).trim() : trimmed;

    const inMatch = body.match(/^([\w.]+)\s+in\s+(.+)$/);
    if (inMatch) {
        const haystack = parseLiteral(inMatch[2]);
        const needle = values[inMatch[1]];
        const found = Array.isArray(haystack) ? haystack.some(candidate => looseEquals(candidate, needle)) : false;
        return negated ? !found : found;
    }

    for (const operator of COMPARISONS) {
        const index = body.indexOf(operator);
        if (index <= 0) continue;
        const name = body.slice(0, index).trim();
        const literal = parseLiteral(body.slice(index + operator.length));
        const actual = values[name];
        const result = operator === "==" || operator === "===" ? looseEquals(actual, literal) : operator === "!=" || operator === "!==" ? !looseEquals(actual, literal) : compare(actual, literal, operator);
        return negated ? !result : result;
    }

    // A bare term is a name read as truthy. Anything that is not even a name is a
    // term this evaluator does not understand, and an unparseable term shows its
    // field: hiding one on a syntax we failed to read would be a silent loss.
    if (!/^[\w.]+$/.test(body)) return true;
    const truthy = Boolean(values[body]);
    return negated ? !truthy : truthy;
}

export function evaluateDisplayCondition(expression: string | null | undefined, values: Record<string, unknown>): boolean {
    if (!expression || !expression.trim()) return true;
    try {
        return expression
            .split("||")
            .some(alternative => alternative.split("&&").every(term => evaluateTerm(term, values)));
    } catch (_error) {
        return true;
    }
}

/** `values` is the scope the condition reads: the form root, or one fieldset row. */
export function isFieldVisible(field: FieldDataType, values: Record<string, unknown> | undefined | null): boolean {
    return evaluateDisplayCondition(field.display_condition, values ?? {});
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Drop `required` so a conditionally hidden field can never block a submission. */
function relaxed(field: FieldDataType): FieldDataType {
    return { ...field, required: false, validators: (field.validators ?? []).filter(validator => validator.type !== "required") };
}

/**
 * The field list a schema should be built from, given the current values: a hidden
 * field is removed, so it is neither validated nor required.
 *
 * A non-repeatable fieldset is resolved against its own nested values. Inside a
 * repeatable one, rows may differ, and a single schema cannot say "required in row 0
 * only" — so a conditional subfield is kept but relaxed. Its own rendering still
 * honours the condition, row by row.
 */
export function resolveVisibleFields(fields: FieldDataType[], values: Record<string, unknown> | undefined | null): FieldDataType[] {
    const scope = values ?? {};
    return fields
        .filter(field => isFieldVisible(field, scope))
        .map(field => {
            if (field.field_type !== "fieldset") return field;
            const subfields = field.subfields ?? [];
            if (field.multiple) {
                return { ...field, subfields: subfields.map(subfield => (subfield.display_condition ? relaxed(subfield) : subfield)) };
            }
            const nested = scope[field.field_name];
            return { ...field, subfields: resolveVisibleFields(subfields, isPlainObject(nested) ? nested : {}) };
        });
}

/** Strip the values of hidden fields, so a masked field is not submitted either. */
export function stripHiddenValues(fields: FieldDataType[], values: Record<string, unknown>): Record<string, unknown> {
    const visible = resolveVisibleFields(fields, values);
    const keep = new Set(visible.map(field => field.field_name));
    const result: Record<string, unknown> = {};
    Object.keys(values).forEach(key => {
        if (keep.has(key)) result[key] = values[key];
    });
    visible.forEach(field => {
        if (field.field_type !== "fieldset" || field.multiple) return;
        const nested = result[field.field_name];
        if (isPlainObject(nested)) {
            result[field.field_name] = stripHiddenValues(field.subfields ?? [], nested);
        }
    });
    return result;
}
